import {
  createAsyncThunk,
  AnyAction,
  PayloadAction,
  createSlice,
} from "@reduxjs/toolkit";
import { EdufinInitialStateDTO } from "./types";
import {
  DownloadEdufin,
  DeleteEdufin,
  GetEdufinData,
  SaveEdufinData,
} from "./integrations.request";
import { setToastNotification } from "../../../../components/ToastNotification/service/toastNotificationSlice";

export const initialState: EdufinInitialStateDTO = {
  downloadEdufinLoading: false,
  downloadEdufinSuccess: false,
  downloadEdufinError: false,
  deleteEdufinLoading: false,
  deleteEdufinSuccess: false,
  deleteEdufinError: false,
  edufinDataLoading: false,
  edufinDataSuccess: false,
  edufinDataError: false,
  edufinData: null,
  saveEdufinLoading: false,
  saveEdufinSuccess: false,
  saveEdufinError: false,
  saveEdufinData: null,
};

export const deleteEdufin = createAsyncThunk(
  "edufin/deleteEdufin",
  async (id: string, thunkAPI: any) => {
    return DeleteEdufin(id)
      .then((response: any) => {
        if (response) {
          thunkAPI.dispatch(
            setToastNotification({
              message: response?.data?.message,
              type: "success",
              snackOpen: true,
            })
          );
        }
        return response?.data?.data;
      })
      .catch(function (e: any) {
        thunkAPI.dispatch(
          setToastNotification({
            message: e?.response?.data?.message,
            type: "error",
            snackOpen: true,
          })
        );
        throw e.response.data;
      });
  }
);

export const downloadEdufin = createAsyncThunk(
  "edufin/downloadEdufin",
  async (data: any, thunkAPI: any) => {
    return DownloadEdufin(data)
      .then((response: any) => {
        if (response) {
          thunkAPI.dispatch(
            setToastNotification({
              message: response?.data?.message,
              type: "success",
              snackOpen: true,
            })
          );
        }
        return response?.data?.data;
      })
      .catch(function (e: any) {
        thunkAPI.dispatch(
          setToastNotification({
            message: e?.response?.data?.message,
            type: "error",
            snackOpen: true,
          })
        );
        throw e.response.data;
      });
  }
);

export const getEdufinData = createAsyncThunk(
  "appIntegrations/getEdufinData",
  async (body: any, thunkAPI: any) => {
    const { instituteId, id } = body;
    return GetEdufinData(instituteId, id)
      .then((response: any) => {
        if (response) {
        }
        return response?.data?.data;
      })
      .catch(function (e: any) {
        throw e.response.data;
      });
  }
);

export const saveEdufinData = createAsyncThunk(
  "edufin/saveEdufinData",
  async (data: any, thunkAPI: any) => {
    return SaveEdufinData(data)
      .then((response: any) => {
        if (response) {
          thunkAPI.dispatch(
            setToastNotification({
              message: response?.data?.message,
              type: "success",
              snackOpen: true,
            })
          );
        }
        return response?.data?.data;
      })
      .catch(function (e: any) {
        thunkAPI.dispatch(
          setToastNotification({
            message: e?.response?.data?.message,
            type: "error",
            snackOpen: true,
          })
        );
        throw e.response.data;
      });
  }
);

export const edufinSlice = createSlice({
  name: "edufin",
  initialState,
  reducers: {
    reset: () => initialState,
    resetEdufinDataSuccess: (state) => {
      state.edufinDataSuccess = false;
    },
    resetEdufinSaveSuccess: (state) => {
      state.saveEdufinSuccess = false;
      state.saveEdufinData = null;
    },
    resetEdufinDeleteSuccess: (state) => {
      state.deleteEdufinSuccess = false;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(
      deleteEdufin.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.deleteEdufinLoading = false;
        state.deleteEdufinSuccess = true;
        state.deleteEdufinError = false;
      }
    );
    builder.addCase(deleteEdufin.rejected, (state, action: AnyAction) => {
      state.deleteEdufinLoading = false;
      state.deleteEdufinSuccess = false;
      state.deleteEdufinError =
        action?.error?.message || "Something went wrong";
    });
    builder.addCase(deleteEdufin.pending, (state) => {
      state.deleteEdufinLoading = true;
      state.deleteEdufinSuccess = false;
      state.deleteEdufinError = false;
    });

    builder.addCase(downloadEdufin.pending, (state) => {
      state.downloadEdufinLoading = true;
      state.downloadEdufinSuccess = false;
      state.downloadEdufinError = false;
    });
    builder.addCase(downloadEdufin.fulfilled, (state, action: AnyAction) => {
      state.downloadEdufinLoading = false;
      state.downloadEdufinSuccess =
        action?.success?.message || "Ozonetel agents updated successfully!";
      state.downloadEdufinError = false;
    });
    builder.addCase(downloadEdufin.rejected, (state, action: AnyAction) => {
      state.downloadEdufinLoading = false;
      state.downloadEdufinSuccess = false;
      state.downloadEdufinError =
        action?.error?.message || "Something went wrong";
    });
    builder.addCase(getEdufinData.pending, (state) => {
      state.edufinDataLoading = true;
      state.edufinDataSuccess = false;
      state.edufinDataError = false;
    });

    builder.addCase(getEdufinData.fulfilled, (state, action: AnyAction) => {
      state.edufinDataLoading = false;
      state.edufinDataSuccess =
        action?.payload?.message || "Stream deleted Successfully";
      state.edufinDataError = false;
      state.edufinData = action?.payload;
    });
    builder.addCase(getEdufinData.rejected, (state, action: AnyAction) => {
      state.edufinDataLoading = false;
      state.edufinDataSuccess = false;
      state.edufinDataError = action?.error?.message || "Something went wrong";
    });
    builder.addCase(saveEdufinData.pending, (state) => {
      state.saveEdufinLoading = true;
      state.saveEdufinSuccess = false;
      state.saveEdufinError = false;
    });

    builder.addCase(saveEdufinData.fulfilled, (state, action: AnyAction) => {
      state.saveEdufinLoading = false;
      state.saveEdufinSuccess = true;
      state.saveEdufinError = false;
      state.saveEdufinData = action?.payload;
    });
    builder.addCase(saveEdufinData.rejected, (state, action: AnyAction) => {
      state.saveEdufinLoading = false;
      state.saveEdufinSuccess = false;
      state.saveEdufinError = action?.error?.message || "Something went wrong";
    });
  },
});

export default edufinSlice.reducer;
export const {
  reset,
  resetEdufinDataSuccess,
  resetEdufinSaveSuccess,
  resetEdufinDeleteSuccess,
} = edufinSlice.actions;
